import React, { useState } from 'react';
import { Text } from '../../UI/Typography';
import { Button } from '../../UI/Buttons';
import Table from '../../UI/Table';
import { deleteDepartment } from '../../../Services/Api/departmentService';
import { usePopup } from '../../../Popups/PopupContext';
import { FaEdit, FaTrashAlt, FaSpinner, FaEye, FaPen } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import DepartmentEditModal from './DepartmentEditModal';
import DepartmentsEmployee from './DepartmentsEmployee';
import AddEmployeeModal from './AddEmployeeModal';

const DepartmntsTable = ({ departments, onRefresh, onEdit }) => {
  const [deletingDeptId, setDeletingDeptId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editingDept, setEditingDept] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [showEmployeesFor, setShowEmployeesFor] = useState(null);
  const [showAddFor, setShowAddFor] = useState(null);
  const { showSuccess, showError } = usePopup();

  const handleEditClick = (dept) => {
    setEditingDept(dept);
    setIsEditOpen(true);
  };

  const handleViewMembers = (dept) => {
    setShowEmployeesFor(dept);
  };

  const handleAddMembers = (dept) => {
    setShowAddFor(dept);
  };

  const handleDeleteClick = (deptId, deptName) => {
    setConfirmDelete({ deptId, deptName });
  };

  const confirmDeleteDept = async () => {
    if (!confirmDelete) return;

    const { deptId, deptName } = confirmDelete;
    setDeletingDeptId(deptId);

    try {
      await deleteDepartment(deptId);
      if (onRefresh) onRefresh();
      showSuccess('Department Deleted', `Department "${deptName}" has been deleted.`);
    } catch (error) {
      showError('Delete Failed', error.message || 'Unable to delete department.');
    } finally {
      setDeletingDeptId(null);
      setConfirmDelete(null);
    }
  };

  const cancelDelete = () => setConfirmDelete(null);

  const columns = [
    // { key: 'dep_id', label: 'ID', align: 'left' },
    { key: 'dep_name', label: 'Name', align: 'left' },
    // { key: 'description', label: 'Description', align: 'left' },
    { key: 'head', label: 'Head', align: 'left' },
    { key: 'members', label: 'Members', align: 'center', width: '120px' },
    { key: 'actions', label: 'Actions', align: 'center', width: '120px' }
  ];

  const renderCell = (dept, column) => {
    if (column.key === 'actions') {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
          <Button
            title={`Edit department ${dept.dep_name}`}
            onClick={() => handleEditClick(dept)}
            disabled={deletingDeptId === dept.dep_id}
            variant="ghost"
            size="small"
          >
            <FaEdit />
          </Button>
          <Button
            title={`Delete department ${dept.dep_name}`}
            onClick={() => handleDeleteClick(dept.dep_id, dept.dep_name)}
            disabled={deletingDeptId === dept.dep_id}
            variant="ghost"
            size="small"
            color="error"
          >
            {deletingDeptId === dept.dep_id ? <FaSpinner className="spin" /> : <FaTrashAlt />}
          </Button>
        </div>
      );
    }

    if (column.key === 'members') {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
          <Button title={`View members of ${dept.dep_name}`} onClick={() => handleViewMembers(dept)} variant="ghost" size="small"><FaEye /></Button>
          <Button title={`Add members to ${dept.dep_name}`} onClick={() => handleAddMembers(dept)} variant="ghost" size="small"><FaPen /></Button>
        </div>
      );
    }

    if (column.key === 'head') {
      const head = dept.head;
      if (!head) return 'N/A';
      // try common head display fields
      return head.first_name || head.username || head.id || 'N/A';
    }

    return dept[column.key] || 'N/A';
  };

  return (
    <div style={{ width: '100%' }}>
      <Table
        columns={columns}
        data={departments || []}
        renderCell={renderCell}
        emptyState={
          <Text style={{ padding: '2rem', textAlign: 'center' }}>
            No departments found.
          </Text>
        }
      />

      <AnimatePresence>
        {confirmDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
            onClick={cancelDelete}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: -40 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: -40 }}
              transition={{ duration: 0.25 }}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                maxWidth: '420px',
                width: '90%',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ margin: '0 0 12px 0', color: '#333' }}>Confirm Delete</h3>
              <p style={{ margin: '0 0 18px 0', color: '#666' }}>
                Are you sure you want to delete the department "{confirmDelete.deptName}"? This action cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <Button variant="secondary" onClick={cancelDelete} style={{ background: '#28A745', color: 'white', border: 'none', cursor: 'pointer' }}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={confirmDeleteDept} style={{ background: '#DC3545', color: 'white', border: 'none', cursor: 'pointer' }}>
                  Delete
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <DepartmentEditModal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        initialData={editingDept || {}}
        onUpdated={() => {
          setIsEditOpen(false);
          setEditingDept(null);
          if (onRefresh) onRefresh();
          showSuccess('Department updated', 'Department was updated successfully.');
        }}
      />
      <DepartmentsEmployee
        isOpen={!!showEmployeesFor}
        onClose={() => setShowEmployeesFor(null)}
        department={showEmployeesFor}
      />

      <AddEmployeeModal
        isOpen={!!showAddFor}
        onClose={() => setShowAddFor(null)}
        departmentId={showAddFor?.dep_id}
        departmentName={showAddFor?.dep_name}
        onAdded={() => { if (onRefresh) onRefresh(); }}
      />
    </div>
  );
};

export default DepartmntsTable;
